import { DraggableProvided } from '@hello-pangea/dnd';
import { Box, Stack, Text } from '@mantine/core';
import { marked } from 'marked';
import { PiDotsSix } from 'react-icons/pi';
import MandatoryMarker from '@/components/common/MandatoryMarker';
import {
  getServiceBlockTiers,
  IServiceBlock,
} from '@/models/commercial-proposals/blocks.store';
import { useChildServices, useService } from '@/models/services';
import EmptyCell from '../../cells/EmptyCell/EmptyCell';
import ServiceNameCell from '../../cells/ServiceNameCell/ServiceNameCell';
import ServicePriceCell from '../../cells/ServicePriceCell/ServicePriceCell';
import ServicePriceDisplay from '../../cells/ServicePriceCell/ServicePriceDisplay';
import ServiceBaseRow from '../ServiceBaseRow/ServiceBaseRow';
import classes from './ServiceMainRow.module.css';

export default function ServiceMainRow({
  provided,
  block,
  service,
  index,
}: {
  provided: DraggableProvided;
  block: IServiceBlock;
  service: IServiceBlock['services'][number];
  index: number;
}) {
  const config = service.config;

  const tiers = getServiceBlockTiers(block.type);
  const { data: serviceDetails, isLoading: serviceDetailsLoading } = useService(
    service.serviceId,
  );
  const { data: childServices, isLoading: childServicesLoading } =
    useChildServices(config.showChildServices ? service.serviceId : 0);

  const mainRow = (provided: DraggableProvided) => (
    <>
      <EmptyCell
        {...provided.dragHandleProps}
        style={{
          textAlign: 'center',
          padding: '3px 3px 0 0',
          color: 'var(--mantine-color-gray-5)',
        }}
      >
        {provided.dragHandleProps && <PiDotsSix size={16} />}
      </EmptyCell>
      <ServiceNameCell
        record={serviceDetails!}
        service={service}
        block={block}
        index={index}
      />
      {Array.from({ length: tiers }).map((_, tier) => (
        <ServicePriceCell
          index={index}
          record={serviceDetails!}
          key={tier}
          tier={tier}
          service={service}
          block={block}
          childServices={childServices}
        />
      ))}
    </>
  );

  const childRow = (child: IServiceBlock['services'][number]) => {
    const record = childServices?.find((s) => s.id == child.serviceId);
    if (!record) {
      console.error('no record', { service, child, childServices });
      return null;
    }
    return (
      <>
        <EmptyCell />
        <EmptyCell p={5}>
          <Text size="xs">
            {record.name}
            <MandatoryMarker shown={record.isMandatory} ml={5} />
          </Text>
          <div
            className="md"
            dangerouslySetInnerHTML={{
              __html: marked(record.description, { breaks: true }),
            }}
          ></div>
        </EmptyCell>
        {Array.from({ length: tiers }).map((_, tier) => (
          <EmptyCell key={`${child.serviceId}-${tier}`}>
            <Box p={5}>
              <ServicePriceDisplay
                config={{ ...config[tier], displayIcon: true }}
                price={child.price}
              />
            </Box>
          </EmptyCell>
        ))}
      </>
    );
  };

  if (serviceDetailsLoading || childServicesLoading) {
    return (
      <Stack
        ref={provided?.innerRef}
        {...provided.draggableProps}
        gap={0}
        className={classes.row}
        style={{ opacity: 0.5 }}
      >
        <ServiceBaseRow>
          <EmptyCell
            {...provided.dragHandleProps}
            style={{
              textAlign: 'center',
              padding: '3px 3px 0 0',
              color: 'var(--mantine-color-gray-5)',
            }}
          >
            {provided.dragHandleProps && <PiDotsSix size={16} />}
          </EmptyCell>
          <EmptyCell p={5}>
            <Text size="xs" c="dimmed">
              Loading...
            </Text>
          </EmptyCell>
          {Array.from({ length: getServiceBlockTiers(block.type) }).map(
            (_, tier) => (
              <EmptyCell key={tier} />
            ),
          )}
        </ServiceBaseRow>
      </Stack>
    );
  }

  return (
    <Stack
      ref={provided?.innerRef}
      {...provided.draggableProps}
      gap={0}
      className={classes.row}
    >
      <ServiceBaseRow>{mainRow(provided)}</ServiceBaseRow>
      {config.showChildServices &&
        childServices &&
        childServices.length > 0 &&
        childServices.map((child, i) => (
          <ServiceBaseRow key={child.id}>
            {childRow({
              blockUuid: block.uuid,
              serviceId: child.id,
              weight: block.weight + i,
              config,
              price: {
                amount: child.price,
                currency: child.currency,
              },
              parentId: service.serviceId,
            })}
          </ServiceBaseRow>
        ))}
    </Stack>
  );
}
